import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { fetchBudgetById, fetchBudgetsList, fetchBudgetUserProfile, fetchBudgetHistory } from '../../../../api/fetchBudget';
import { IBudgetFormData, BudgetRecipientEnum } from '../../createAndEdit/types';
import { Budget } from '../../types';
import { useBudgetViewMode } from './useBudgetViewMode';

function useBudgetVersion(props: {mainRoute: string}) {
  const { budgetId, version } = useParams<{budgetId: string, version: string}>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [budgetVersion, setBudgetVersion] = useState<IBudgetFormData | null>(null);
  const [budget, setBudget] = useState<Budget>();
  const [userProfile, setUserProfile] = useState<BudgetRecipientEnum | string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastVersionNumber, setLastVersionNumber] = useState<number | undefined>(undefined);
  const [currentVersionNumber, setCurrentVersionNumber] = useState<number | undefined>(undefined);

  const versionNumber = useMemo(() => {
    if (!version) return undefined;
    if (version.toLowerCase() === 'latest') return undefined;
    const parsed = parseInt(version, 10);
    return isNaN(parsed) ? undefined : parsed;
  }, [version]);

  useEffect(() => {
    const loadBudgetVersion = async () => {
      if (!budgetId) {
        setLoading(false);
        return;
      }

      if (!version) {
        setLoading(false);
        navigate(props.mainRoute);
        return;
      }

      try {
        setLoading(true);
        const isLatest = version.toLowerCase() === 'latest';
        let versionToUse: number | undefined;

        const profile = await fetchBudgetUserProfile();
        if (profile) {
          setUserProfile(profile);
        }

        if (isLatest) {
          try {
            const historyResponse = await fetchBudgetHistory(budgetId, 'versionNumber', 'desc', 1, 1, true);
            const versions = historyResponse?.data || [];
            if (!versions || versions.length === 0) {
              setLoading(false);
              navigate(props.mainRoute);
              return;
            }
            const sortedVersions = versions.sort((a, b) => b.version - a.version);
            versionToUse = sortedVersions[0].version;
            setLastVersionNumber(versionToUse);
            setCurrentVersionNumber(versionToUse);
          } catch (historyError) {
            console.error('Erro ao buscar histórico de versões:', historyError);
            setLoading(false);
            navigate(props.mainRoute);
            return;
          }
        } else {
          const parsedVersion = parseInt(version, 10);
          if (isNaN(parsedVersion)) {
            setLoading(false);
            navigate(props.mainRoute);
            return;
          }
          try {
            const historyResponse = await fetchBudgetHistory(budgetId, 'versionNumber', 'desc', 1, 100, true);
            const versions = historyResponse?.data || [];
            if (versions && versions.length > 0) {
              const sortedVersions = versions.sort((a, b) => b.version - a.version);
              setLastVersionNumber(sortedVersions[0].version);
              const versionExists = versions.some(v => v.version === parsedVersion);
              if (!versionExists) {
                setLoading(false);
                navigate(props.mainRoute);
                return;
              }
            } else {
              setLoading(false);
              navigate(props.mainRoute);
              return;
            }
          } catch (historyError) {
            console.error('Erro ao buscar histórico de versões:', historyError);
            setLoading(false);
            navigate(props.mainRoute);
            return;
          }
          versionToUse = parsedVersion;
          setCurrentVersionNumber(versionToUse);
        }

        if (!versionToUse) {
          setLoading(false);
          navigate(props.mainRoute);
          return;
        }

        const occurrenceId = searchParams.get('occurrenceId') || '';
        const data = await fetchBudgetById(budgetId, occurrenceId, versionToUse);
        
        if (!data) {
          setLoading(false);
          navigate(props.mainRoute);
          return;
        }

        setBudgetVersion(data);

        setBudget({
          id: data.budgetId,
          consultantName: data.consultant || '',
          origin: data.origin || '',
          recipient: data.recipient || '',
          user: data.username || '',
          version: currentVersionNumber || versionNumber || data.versionNumber || 1,
          date: new Date().toLocaleDateString('pt-BR'),
          status: data.status || '',
          hasHistory: false,
          lastVersionNumber: currentVersionNumber || versionNumber || data.versionNumber || 1,
        });

        if (data.occurrenceId) {
          const budgetsListResponse = await fetchBudgetsList(data.occurrenceId);
          const budgetItem = budgetsListResponse?.budgets.find((item) => item.uuid === budgetId);

          setBudget({
            id: budgetItem?.uuid || data.budgetId,
            consultantName: budgetItem?.consultant || data?.consultant || '',
            origin: budgetItem?.origin || data?.origin || '',
            recipient: budgetItem?.recipient || data?.recipient || '',
            user: budgetItem?.username || data?.username || '',
            version: currentVersionNumber || versionNumber || budgetItem?.lastVersionNumber || 1,
            date: new Date(budgetItem?.createdAt || '').toLocaleDateString('pt-BR'),
            status: budgetItem?.status || data?.status || '',
            hasHistory: true,
            lastVersionNumber: budgetItem?.lastVersionNumber || currentVersionNumber || versionNumber || data?.versionNumber || 1,
          });
        }
      } catch (err) {
        setLoading(false);
        navigate(props.mainRoute);
      } finally {
        setLoading(false);
      }
    };

    loadBudgetVersion();
  }, [budgetId, version, searchParams, navigate, props.mainRoute, versionNumber]);

  const viewMode = useBudgetViewMode({
    lastVersionNumber,
    budget,
    userProfile,
    versionNumber: currentVersionNumber || versionNumber,
  });

  const afterSave = () => {
    navigate(props.mainRoute);
  };

  return {
    budgetVersion,
    budget,
    userProfile,
    viewMode,
    loading,
    afterSave,
  };
}

export default useBudgetVersion;